<template>
  <div v-resize="calculateChartWindowSize" tabindex="0" @keydown.esc="closeOverlays=false">
    <sidedrawer
      :drawer="drawer"
      :selectedDimension="selectedDimension"
      :selectedComplement="selectedComplement"
      :filterDimension="filterDimension"
      @update:filterDimension="filterDimension=$event"
      @update:drawer:width="drawerWidth=$event"
      @update:drawer:value="drawer=$event"
    >
    </sidedrawer>
    <v-toolbar
      id="main-menubar"
      app
      clipped-left
      flat
      color="secondary"
    >
      <v-toolbar-title>
        <v-layout>
          <v-flex xs12 sm6>
            Explore
            <!-- indicator selector -->
            <v-menu
              id = "indicator-selector"
             origin="center center"
             transition="scale-transition"
             bottom
             offset-y
             :max-height="mainSelectsHeight"
             class="scroll-y overflow-y-auto"
            >
              <v-btn slot="activator">
                {{selectedIndicator.text}}
              </v-btn>
              <v-list dense>
                <template v-for="(i, index) in indicators">
                  <v-subheader
                    class="indicator-subheader"
                    inset
                    v-if="i.header"
                    :key="i.header"
                  >
                    {{i.header}}
                  </v-subheader>
                  <!-- tooltips are only shown for indicators, not headlines in the indicators dropdown -->
                  <v-tooltip
                    v-else-if="typeof i.header === 'undefined'"
                    :disabled="i.description === null"
                    :key="index"
                    right
                    :allow-overflow="true"
                    max-width="300"
                  >
                    <div slot="activator">
                      <v-list-tile
                        @click="selectedIndicator = i"
                      >
                        <v-list-tile-content>
                          {{i.text}}
                        </v-list-tile-content>
                      </v-list-tile>
                    </div>
                      {{i.description}}
                  </v-tooltip>
                </template>
              </v-list>
            </v-menu>
            <!-- normalisation selector -->
            <v-menu
             origin="center center"
             transition="scale-transition"
             bottom
             offset-y
             :max-height="mainSelectsHeight"
             class="scroll-y overflow-y-auto"
            >
              <v-btn id="normalization-selector" slot="activator">
                {{selectedNormalization.id === constants.NO_NORM.id ? 'not normalised' : ' ' + selectedNormalization.value}}
              </v-btn>
              <v-list dense>
                <template
                  v-if="Array.isArray(selectedIndicator.norm_by)"
                  v-for="(n, index) in selectedIndicator.norm_by"
                >
                  <v-tooltip
                    :disabled="typeof n.description === 'undefined'"
                    :key="'normalization_option_'+index"
                    right
                    :allow-overflow="true"
                    max-width="300"
                  >
                    <div slot="activator">
                      <v-list-tile
                        @click="selectedNormalization = n"
                      >
                        <v-list-tile-content>
                          {{ n.value }}
                        </v-list-tile-content>
                      </v-list-tile>
                    </div>
                    <!-- placeholder for normalisation description not yet present in API -->
                      {{ n.description }}
                  </v-tooltip>
                </template>
              </v-list>
            </v-menu>
            , grouped by
            <!-- dimension type selector -->
            <v-menu
              id="level-selector"
             origin="center center"
             transition="scale-transition"
             bottom
             offset-y
             :max-height="mainSelectsHeight"
             class="scroll-y overflow-y-auto"
            >
              <v-btn slot="activator">
                {{ selectedDimension.name }}
              </v-btn>
              <v-list dense>
                <template v-for="(g, index) in constants.DIMENSION_GROUPS">
                  <v-subheader inset :key="g.header + index">{{g.header}}</v-subheader>
                  <v-list-tile v-for="(d, index2) in constants.DIMENSIONS.filter(d => d.dimensionType === g.dimensionType)"
                    :key="d.name + index2" @click="selectItems(d, 'dimension')" :disabled="disableItems(d, 'dimension')">
                    <v-list-tile-content>
                      {{d.name}}
                    </v-list-tile-content>
                  </v-list-tile>
                </template>
              </v-list>
            </v-menu>
            in
            <!-- year selector -->
            <v-menu
              origin="center center"
              transition="scale-transition"
              bottom
              offset-y
              :max-height="mainSelectsHeight"
              class="scroll-y overflow-y-auto"
            >
              <template slot="activator" slot-scope="{ on: menu }">
                <v-tooltip
                  left
                  :disabled="selectedYear.alert === false"
                >
                  <template slot="activator" slot-scope="{ on: tooltip }">
                    <span v-on="tooltip">
                      <v-btn v-on="menu">
                        <span>{{ selectedYear.year }}</span>
                      </v-btn>
                    </span>
                  </template>
                  <span v-if="selectedYear.alert"><v-icon dark small>warning</v-icon> {{ selectedYear.message }}</span>
                </v-tooltip>
              </template>
              <v-list dense>
                <v-tooltip
                  left
                  :disabled="y.alert === false"
                  v-for="(y, yindex) in years"
                  :key="yindex"
                >
                <v-list-tile
                  slot="activator"
                  class="year-container"
                  @click="selectedYear = y"
                >
                  <v-list-tile-title class="year" >
                    {{ y.year }}
                  </v-list-tile-title>
                </v-list-tile>
                  <span v-if="y.alert"><v-icon dark small>warning</v-icon> {{ y.message }}</span>

                </v-tooltip>
              </v-list>
            </v-menu>
          </v-flex>
        </v-layout>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <!-- menu buttons top right -->
      <v-tooltip
        id="goto-landingpage"
        bottom
      >
        <v-btn slot="activator" icon to="/">
          <v-icon>home</v-icon>
        </v-btn>
        Go to start page
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn slot="activator" icon @click="showDownloadDialog = true">
          <v-icon>cloud_download</v-icon>
        </v-btn>
        Download dialogue
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn slot="activator" icon
               @click="showTour = !showTour">
          <v-icon>directions</v-icon>
        </v-btn>
        Introductory tour
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn slot="activator" icon
          @click="toggleStartDialog"
        >
          <v-icon>help</v-icon>
        </v-btn>
        About
      </v-tooltip>
    </v-toolbar>

    <v-content>
        <v-layout>
          <v-flex xs12>
            <v-toolbar
              id="secondary-menubar"
              color="white"
            >
              <v-toolbar-title>
                Show
                <!-- complement dimension selector -->
                <v-menu
                  id="complement-selector"
                  origin="center center"
                  transition="scale-transition"
                  bottom
                  offset-y
                  :max-height="secondarySelectsHeight"
                  class="scroll-y overflow-y-auto"
                >
                  <v-btn slot="activator">
                    {{selectedComplement.name}}
                  </v-btn>
                  <v-list dense>
                    <template v-for="g in constants.DIMENSION_GROUPS">
                      <v-subheader inset :key="g.header">{{g.header}}</v-subheader>
                      <v-list-tile v-for="d in constants.DIMENSIONS.filter(d => d.dimensionType === g.dimensionType)"
                        :key="d.name" @click="selectItems(d, 'complement')" :disabled="disableItems(d, 'complement')">
                        <v-list-tile-content>
                          {{d.name}}
                        </v-list-tile-content>
                      </v-list-tile>
                    </template>
                  </v-list>
                </v-menu>
                <!-- complement dimension grouping -->
                <v-menu
                  origin="center center"
                  transition="scale-transition"
                  bottom
                  offset-y
                  :max-height="secondarySelectsHeight"
                  class="scroll-y overflow-y-auto"
                  :close-on-content-click="false"
                >
                  <v-btn slot="activator">{{selectedComplementItem.text}}</v-btn>
                  <v-list dense>
                    <v-list-tile @click="selectedComplementItem = constants.AGGREGATE_COMPLEMENTS">
                      <v-list-tile-content>
                        {{constants.AGGREGATE_COMPLEMENTS.text}}
                      </v-list-tile-content>
                    </v-list-tile>
                    <!--
                    <v-list-tile @click="selectedComplementItem = aggregateSelectedComplementItem">
                      <v-list-tile-content>
                        {{aggregateSelectedComplementItem.text}}
                      </v-list-tile-content>
                    </v-list-tile>
                    -->
                    <v-list-tile
                    @click="selectedComplementItem = constants.EACH_SELECTED_COMPLEMENT">
                      <v-list-tile-content>
                        each (selected)
                      </v-list-tile-content>
                      <v-list-tile-action>
                        <v-btn icon disabled>
                          <v-icon>bar_chart</v-icon>
                          <v-icon>table_chart</v-icon>
                        </v-btn>
                      </v-list-tile-action>
                    </v-list-tile>
                    <v-list-tile @click="editSelection()">
                      <v-list-tile-content>
                        edit selection ...
                      </v-list-tile-content>
                    </v-list-tile>
                    <v-divider></v-divider>
                    <v-subheader inset>Selection</v-subheader>
                    <v-list-tile v-for="item in selectedItems" :key="item.code" @click="selectedComplementItem = item">
                      <v-list-tile-content>
                        {{item.text}}
                      </v-list-tile-content>
                    </v-list-tile>
                    <v-divider></v-divider>
                    <v-list-tile v-if="allSelectedItems.length > displaySelectedCount" @click="displayMoreSelected()">
                      <v-list-tile-content>
                        ... show more
                      </v-list-tile-content>
                    </v-list-tile>
                  </v-list>
                </v-menu>
                <span v-if="dataLoading">
                  <v-progress-circular
                    :size="20"
                    indeterminate
                  ></v-progress-circular>
                  loading data
                </span>
                <span style="color: red;" v-if="noData">
                  <v-icon color="red">
                  warning
                  </v-icon>
                    no data to display <span v-if="emptyFilter">(empty filter)</span>
                </span>
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-container fluid justify-end fill-height ma-0 pa-0  style="height: 64px; max-width: 300px">
              <v-toolbar-items>
                <!--<v-tooltip bottom disabled="selectedDimension.dimensionType != 'topic'"> -->
                <v-btn flat
                       id="activate-map"
                       :disabled="selectedDimension.dimensionType === 'topic' || selectedDimension.dimensionType === 'location' && selectedComplementItem.value === 'each'"
                       active-class="grey lighten-2"
                       :input-value="selectedVisualization === 'activate-map'"
                       @click="selectedVisualization = 'activate-map'"
                       style="max-width: 100px"
                       large
                >
                  <v-icon>map</v-icon>
                  Map
                </v-btn>
                <!-- The map is not available for this configuration.
              </v-tooltip>-->
                <v-btn flat
                       active-class="grey lighten-2"
                       :input-value="selectedVisualization === 'activate-barchart'"
                       @click="selectedVisualization = 'activate-barchart'"
                       style="max-width: 100px"
                       large>
                  <v-icon>bar_chart</v-icon>
                  Bar
                </v-btn>
                <v-btn id="activate-table"
                       flat active-class="grey lighten-2"
                       :input-value="selectedVisualization === 'activate-table'"
                       @click="selectedVisualization = 'activate-table'"
                       style="max-width: 100px"
                       large>
                  <v-icon>table_chart</v-icon>
                  Table
                </v-btn>
              </v-toolbar-items>
              </v-container>
            </v-toolbar>
            <div>
              <v-layout fill-height>
                <v-flex xs12>
                  <charts
                    :vis-width="chartContainerWidth"
                    :vis-height="chartContainerHeight"
                    :fact-sheet="factSheet"
                    @click:item="clickItem($event)"
                  >
                  </charts>
                </v-flex>
              </v-layout>
            </div>
          </v-flex>
        </v-layout>
    </v-content>
    <v-snackbar
      :timeout="10000"
      top
      v-model="serverError"
      color="grey"
    >
      Requested indicator/filters yielded a server-side error (HTTP 5xx code).<br>
      Please report to dev@knowmak.eu
    </v-snackbar>
    <start-dialog :dialogContents="dialogContents">
    </start-dialog>
    <download-dialog v-if="showDownloadDialog" :active="showDownloadDialog" @update:active="showDownloadDialog = $event"></download-dialog>
    <tour :showTour="showTour" @toggleTour="showTour = $event"></tour>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import constants from '../constants.js'
import Sidedrawer from './Sidedrawer'
import Charts from './Charts'
import StartDialog from './StartDialog'
import Tour from './Tour'
import startDialogJSON from '@/assets/data/startdialog.json'

// TODO: Fix the v-container for a dynamic alignment with the fact sheet width

const DownloadDialog = () => import('@/components/DownloadDialog')
const Help = () => import('@/components/HelpMenu')

export default {
  name: 'Dashboard',
  components: {
    Sidedrawer,
    Charts,
    Help,
    StartDialog,
    DownloadDialog,
    Tour
  },
  data () {
    return {
      drawer: false,
      drawerWidth: 0,
      chartContainerWidth: 0,
      chartContainerHeight: 0,
      mainSelectsHeight: 100,
      secondarySelectsHeightDefault: 400,
      secondarySelectsHeight: this.secondarySelectsHeightDefault,
      factSheet: true,
      snackbar: true,
      defaultDisplaySelectedCount: 5,
      displaySelectedCount: 5,
      filterDimension: null,
      showDownloadDialog: false
    }
  },
  computed: {
    ...mapState(['years', 'rootTopics', 'topics', 'subtopics', 'indicators', 'profile']),
    dialogContents () {
      return startDialogJSON
    },
    closeOverlays: {
      get () {
        return this.$store.showFactSheet
      },
      set (visVal) {
        this.$store.commit('setShowFactSheet', visVal)
        this.closeFilter()
      }
    },
    constants: {
      get () {
        return constants
      }
    },
    selectedYear: {
      get () {
        // return this.$store.getters.getSelectedYear
        return this.$store.state.selectedYear
      },
      set (selectedYear) {
        this.$store.dispatch('selectYear', selectedYear)
      }
    },
    selectedIndicator: {
      get () {
        return this.$store.state.selectedIndicator
      },
      set (selectedIndicator) {
        this.$store.dispatch('selectIndicator', {selectedIndicator: selectedIndicator})
      }
    },
    selectedNormalization: {
      get () {
        return this.$store.state.selectedNormalization
      },
      set (selectedNormalization) {
        this.$store.dispatch('selectNormalization', {selectedNormalization: selectedNormalization})
      }
    },
    emptyFilter: {
      get () {
        return this.$store.getters.getDimensionItems(this.selectedDimension, true).length === 0 ||
          this.$store.getters.getDimensionItems(this.selectedComplement, true).length === 0
      }
    },
    selectedDimension: {
      get () {
        return this.$store.state.selectedDimension
      },
      set (selectedDimension) {
        this.$store.dispatch('selectDimension', selectedDimension)
      }
    },
    selectedComplement: {
      get () {
        return this.$store.state.selectedComplement
      },
      set (selectedComplement) {
        this.displaySelectedCount = this.defaultDisplaySelectedCount
        this.$store.dispatch('selectComplement', selectedComplement)
      }
    },
    selectedComplementItem: {
      get () {
        return this.$store.state.selectedComplementItem
      },
      set (selectedComplementItem) {
        this.$store.dispatch('selectComplementItem', {'complementItem': selectedComplementItem, 'loadData': true})
      }
    },
    badRequest: {
      get () {
        return this.$store.state.badRequest
      },
      set (badRequest) {
        this.$store.commit('setBadRequest', badRequest)
      }
    },
    serverError: {
      get () {
        return this.$store.state.serverError
      },
      set (serverError) {
        this.$store.commit('setServerError', serverError)
      }
    },
    dataLoading: {
      get () {
        return this.$store.state.dataLoading
      },
      set (dataLoading) {
        this.$store.commit('setDataLoading', dataLoading)
      }
    },
    rootTopicCodes () {
      return this.rootTopics.map(t => t.code)
    },
    allSelectedItems () {
      return this.$store.getters.getDimensionItems(this.selectedComplement, true)
    },
    selectedItems () {
      return this.allSelectedItems.slice(0, this.displaySelectedCount)
    },
    DTO: {
      get () {
        return this.$store.getters.getDTO
      }
    },
    emptyDTO: {
      get () {
        let result = Object.assign({}, this.DTO).data.find(d => d.year === this.selectedYear.year) === undefined
        return result
      }
    },
    noData: {
      get () {
        return !this.dataLoading && this.emptyDTO
      }
    },
    selectedVisualization: {
      get () {
        return this.$store.state.selectedVisualization
      },
      set (selectedVisualization) {
        this.$store.dispatch('selectVisualization', selectedVisualization)
      }
    },
    showTour: {
      get () {
        return this.$store.state.showTour
      },
      set (showTour) {
        this.$store.commit('setTourState', showTour)
      }
    }
  },
  methods: {
    ...mapActions(['initSelection', 'toggleStartDialog', 'initAppVersion']),
    // disable invalid dropdown items
    disableItems (i, type) {
      switch (type) {
        case 'dimension':
          // TODO produces an error on initial selection because no indicator is defined yet
          // and so no invalidDimensionTypes are available
          // this also looks like it should be moved to computed?! not run manually?
          if (this.selectedIndicator !== undefined && this.selectedIndicator.invalidDimensionTypes !== undefined) {
            return this.selectedIndicator.invalidDimensionTypes.includes(i.dimensionType)
          } else {
            return false
          }
        case 'complement':
          return this.selectedDimension.dimensionType === i.dimensionType
        default:
          return false
      }
    },
    // Necessary to close the sidedrawer
    closeFilter () {
      this.$root.$emit('Sidedrawer')
    },
    // make disabled dropdown items actually not clickable
    selectItems (i, type) {
      if (this.disableItems(i, type) !== true) {
        switch (type) {
          case 'dimension':
            this.selectedDimension = i
            break
          case 'complement':
            this.selectedComplement = i
            break
          default:
            break
        }
      }
    },
    clickItem (clickedCode) {
      if (clickedCode.firstDimCode !== this.profile.code || clickedCode.complementCode !== this.profile.complementItem.code) {
        // only reload the profile if the user actually clicked on something new
        this.$store.dispatch('loadProfile', {code: clickedCode.firstDimCode, complement: clickedCode.complementCode})
      }
      // TODO this needs fixing – close the fact sheet when the same code was clicked
      this.$store.commit('setShowFactSheet', true)
    },
    editSelection () {
      this.filterDimension = this.selectedComplement.dimensionType
      this.drawer = true
    },
    displayMoreSelected () {
      this.displaySelectedCount += 3
    },
    calculateChartWindowSize () {
      // calculate width / height of:
      // filter side drawer, div containing visualisations, select drop downs
      this.chartContainerWidth = window.innerWidth - this.drawerWidth
      let toolBarHeight = parseInt(document.getElementsByClassName('v-toolbar__content')[0].style.height)
      this.chartContainerHeight = window.innerHeight - 2 * toolBarHeight
      this.mainSelectsHeight = window.innerHeight - toolBarHeight - 20

      if (this.chartContainerHeight < this.secondarySelectsHeightDefault) {
        this.secondarySelectsHeight = this.chartContainerHeight - 20
      } else {
        this.secondarySelectsHeight = this.secondarySelectsHeightDefault
      }
    }
  },
  // lifecycle hooks
  created: function () {
    this.selectedComplementItem = constants.AGGREGATE_COMPLEMENTS
    this.selectedComplement = constants.DEFAULT_COMPLEMENT
    this.selectedDimension = constants.DEFAULT_DIMENSION // overwrite the complement

    this.initSelection() // this.$store.dispatch('initSelection')
    this.initAppVersion(this.dialogContents.vers)
    // this.filterDimension = this.selectedDimension.dimensionType
  },
  mounted () {
    this.$nextTick(() => {
      this.calculateChartWindowSize()
      window.addEventListener('resize', () => {
        this.calculateChartWindowSize()
      })
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', window)
  }
}
</script>

<style scoped>
  >>> #secondary-menubar .v-toolbar__content {
    padding-right: unset;
  }

  .indicator-subheader {
    text-transform: capitalize
  }

  >>> .year-container > a.v-list__tile {
    justify-content: flex-end;
  }
  .year {
    padding-left: 5px;
    text-align: center;
  }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard.vue